/**
 * Reine Logik des Benachrichtigungsmenüs der Glocke (Musterkatalog M-068, Design-Guide `glocke.md`). Keine Rechte-Entscheidung:
 * welche Benachrichtigungen die Person sieht und markieren darf, entscheiden die Server-Actions (nur eigene, Sitzung, SG-1/SG-2);
 * dieser Baustein bekommt nur das Ergebnis. Gelesen-Stand und Zähler gehören nur der eigenen Person (r176/r177).
 */

/** Herkunftsart einer Benachrichtigung (aus der Ereignisart abgeleitet, Server); bestimmt nur das Symbol im Kreis. */
export type GlockeArt = "konto" | "kantine" | "besprechung" | "vertretung" | "projekt" | "chat" | "system";

export interface GlockeEintrag {
  id: string;
  art: GlockeArt;
  titel: string;
  /** Kurztext (höchstens 240 Zeichen) oder `null`. */
  text: string | null;
  /** Portalpfad (beginnt mit „/“, vom Server geprüft) oder `null`: dann markiert der Klick nur als gelesen. */
  ziel: string | null;
  /** ISO-Zeitpunkt. */
  erstellt: string;
  gelesen: boolean;
}

/** Antwortform der Server-Actions (Ablehnung über das Ziel, Fehlschlag über den Vorgang, r105; nie ein roher Code). */
export type GlockeAntwort<T> = ({ ok: true } & T) | { ok: false; art: "abgelehnt" | "fehlgeschlagen"; meldung: string };

export interface GlockeAktionen {
  /** Die jüngsten eigenen Benachrichtigungen (höchstens 20); `nurUngelesen` für den Reiter „Ungelesen“. */
  liste: (eingabe: { nurUngelesen: boolean }) => Promise<GlockeAntwort<{ eintraege: GlockeEintrag[]; ungelesen: number }>>;
  /** Eine eigene Benachrichtigung als gelesen markieren. */
  markiereGelesen: (id: string) => Promise<GlockeAntwort<{ ungelesen: number }>>;
  /** Alle eigenen als gelesen markieren. */
  markiereAlleGelesen: () => Promise<GlockeAntwort<{ ungelesen: number }>>;
}

export interface GlockeMenueProps {
  /** Ungelesene Benachrichtigungen der eigenen Person (billiger Zähler aus dem Layout). */
  ungelesen: number;
  aktionen: GlockeAktionen;
  /** Ziel des Zahnrads im Kopf (Einstellungen). */
  einstellungenHref: string;
  /** Ziel von „Alle anzeigen“ (vorerst die Altfläche, bis es eine Vollseite gibt). */
  alleHref: string;
  ton?: "hell" | "dunkel";
  /**
   * Darf das Menü Benachrichtigungen als gelesen markieren? Das Layout setzt `false` unter Impersonation: der Gelesen-Stand gehört der
   * echten Person, die Actions lehnen das Markieren dort ab (r177). Anbieten ist nie großzügiger als Erlauben (r110): dann fehlen
   * „Alle als gelesen markieren“ und die Markier-Schaltfläche der Einträge ohne Ziel, statt nach dem Klick einen Ablehnungssatz zu zeigen.
   * Vorgabe `true`.
   */
  markierenErlaubt?: boolean;
}

/** Feste Sätze (r105: Ablehnung über das Ziel, Fehlschlag über den Vorgang, nie derselbe Satz, ohne Code). */
export const GLOCKE_ABGELEHNT_SATZ = "Diese Benachrichtigungen stehen nicht zur Verfügung.";
export const GLOCKE_FEHLSCHLAG_LISTE = "Die Benachrichtigungen konnten nicht geladen werden. Bitte erneut versuchen.";
export const GLOCKE_FEHLSCHLAG_MARKIEREN = "Die Benachrichtigungen konnten nicht als gelesen markiert werden. Bitte erneut versuchen.";

/** Höchstzahl der Einträge im Menü. */
export const GLOCKE_LIMIT = 20;

/** Zähler am Symbol: ab 10 „9+“; 0 und ungültige Werte zeigen keinen Zähler (`null`). */
export function zaehlerText(n: number): string | null {
  if (!Number.isFinite(n) || n <= 0) return null;
  return n > 9 ? "9+" : String(Math.floor(n));
}

/** Zugänglicher Name der Glocke (die genaue Zahl, nicht die gekürzte). */
export function glockeLabel(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "Benachrichtigungen, keine neuen";
  return `Benachrichtigungen, ${Math.floor(n)} ungelesen`;
}

/** Nur Portalpfade sind ein Ziel (kein Protokoll, keine Doppel-Schrägstriche, keine Rückwärts-Schrägstriche). */
export function sichererZielpfad(link: string | null | undefined): string | null {
  if (typeof link !== "string") return null;
  if (link.length === 0 || link.length > 2000) return null;
  if (!link.startsWith("/") || link.startsWith("//") || link.startsWith("/\\")) return null;
  // eslint-disable-next-line no-control-regex -- Steuerzeichen im Pfad sind nie ein Ziel
  if (/[\u0000-\u001f\u007f]/.test(link)) return null;
  return link;
}

/** Antwortsatz einer fehlgeschlagenen Action: Ablehnung und Fehlschlag tragen ihren eigenen Satz, nie einen Code. */
export function satzFuerAntwort(a: { art: "abgelehnt" | "fehlgeschlagen"; meldung: string }, fehlschlag: string): string {
  return a.art === "abgelehnt" ? GLOCKE_ABGELEHNT_SATZ : fehlschlag;
}

const MINUTE = 60_000;
const STUNDE = 3_600_000;

function berlinTag(d: Date): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}

function tageZwischen(a: Date, b: Date): number {
  const tag = (d: Date) => Date.UTC(Number(berlinTag(d).slice(0, 4)), Number(berlinTag(d).slice(5, 7)) - 1, Number(berlinTag(d).slice(8, 10)));
  return Math.round((tag(a) - tag(b)) / 86_400_000);
}

/**
 * T14: Ereignisse bis 7 Tage zurück relativ („gerade eben“, „vor 5 Min“, „vor 3 Std“, „gestern“, „vor 4 Tagen“), danach absolut
 * „TT.MM.JJJJ“ (Zeitzone Europe/Berlin). Zukunft und Unlesbares: absolut bzw. leer.
 */
export function zeitText(iso: string, jetzt: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diff = jetzt.getTime() - d.getTime();
  const absolut = new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric" }).format(d);
  if (diff < 0) return absolut;
  const tage = tageZwischen(jetzt, d);
  if (tage === 0) {
    if (diff < MINUTE) return "gerade eben";
    if (diff < STUNDE) return `vor ${Math.floor(diff / MINUTE)} Min`;
    return `vor ${Math.floor(diff / STUNDE)} Std`;
  }
  if (tage === 1) return "gestern";
  if (tage >= 2 && tage <= 7) return `vor ${tage} Tagen`;
  return absolut;
}

/** Vollständiger Zeitpunkt für `title` und `<time>` (Europe/Berlin). */
export function zeitVoll(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(d);
}
